/**
 * Error Reporter Utility
 * Captures and reports errors to backend error tracking system
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

class ErrorReporter {
  constructor() {
    this.isReporting = false; // Prevent circular error reporting
    this.errorQueue = [];
    this.maxQueueSize = 10;
    this.reportingEnabled = true;
  }

  /**
   * Report error to backend
   */
  async reportError(errorData) {
    // Prevent circular error reporting
    if (this.isReporting) {
      console.warn('[ErrorReporter] Circular error detected, skipping');
      return;
    }

    // Check if reporting is enabled
    if (!this.reportingEnabled) {
      return;
    }

    try {
      this.isReporting = true;

      // Add to queue if not already at capacity
      if (this.errorQueue.length >= this.maxQueueSize) {
        console.warn('[ErrorReporter] Error queue full, dropping oldest error');
        this.errorQueue.shift();
      }

      this.errorQueue.push(errorData);

      // Send to backend
      const response = await fetch(`${API_BASE}/api/errors/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      });

      if (response.ok) {
        // Remove from queue on successful report
        this.errorQueue = this.errorQueue.filter(e => e !== errorData);
      } else {
        console.error('[ErrorReporter] Failed to report error:', response.statusText);
      }
    } catch (error) {
      // Silently fail - don't break the app if error reporting fails
      console.error('[ErrorReporter] Error reporting failed:', error.message);
    } finally {
      this.isReporting = false;
    }
  }

  /**
   * Capture JavaScript error with context
   */
  captureError(error, level = 'error', additionalContext = {}) {
    const errorData = {
      level,
      error_type: 'frontend',
      exception_type: error.name || 'Error',
      message: error.message || String(error),
      stack_trace: error.stack || null,
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: this.getUserId(),
      ...additionalContext,
    };

    this.reportError(errorData);
  }

  /**
   * Capture exception from React Error Boundary
   */
  captureException(error, errorInfo) {
    const errorData = {
      level: 'error',
      error_type: 'frontend',
      exception_type: error.name || 'ReactError',
      message: error.message || String(error),
      stack_trace: error.stack || errorInfo?.componentStack || null,
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: this.getUserId(),
      component_stack: errorInfo?.componentStack,
    };

    this.reportError(errorData);
  }

  /**
   * Capture API error
   */
  captureAPIError(error, endpoint, method = 'GET') {
    const errorData = {
      level: 'error',
      error_type: 'frontend',
      exception_type: 'APIError',
      message: `API Error: ${method} ${endpoint} - ${error.message || 'Unknown error'}`,
      stack_trace: error.stack || null,
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: this.getUserId(),
      api_endpoint: endpoint,
      api_method: method,
      status_code: error.response?.status,
    };

    this.reportError(errorData);
  }

  /**
   * Get current user ID from localStorage or context
   */
  getUserId() {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Decode JWT to get user ID (simple base64 decode of payload)
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || payload.user_id || payload.username || null;
      }
    } catch (e) {
      // Silently fail
    }
    return null;
  }

  /**
   * Disable error reporting (useful for development)
   */
  disable() {
    this.reportingEnabled = false;
  }

  /**
   * Enable error reporting
   */
  enable() {
    this.reportingEnabled = true;
  }
}

// Singleton instance
const errorReporter = new ErrorReporter();

// Setup global error handler
if (typeof window !== 'undefined') {
  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    errorReporter.captureError(event.error || {
      name: 'UnhandledError',
      message: event.message,
      stack: `at ${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorReporter.captureError(event.reason || {
      name: 'UnhandledPromiseRejection',
      message: String(event.reason),
    });
  });
}

export default errorReporter;
